<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    // eslint-disable-next-line no-console
    console.log('[audio-debug] AudioRecorder:record-end:received', {
      blobType: blob?.type,
      blobSize: blob?.size,
      mediaRecorderMimeType: record.value?.mediaRecorder?.mimeType,
      mediaRecorderState: record.value?.mediaRecorder?.state,
      requestedFormat: props.audioRecordFormat,
      userAgent: navigator?.userAgent,
    });
    try {
      const audioUrl = URL.createObjectURL(blob);
      // eslint-disable-next-line no-console
      console.log('[audio-debug] AudioRecorder:record-end:objectUrlCreated', {
        audioUrl,
      });
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      // A EXTENSÃO SEGUE O FORMATO, e antes era `.mp3` fixo — inclusive quando o conteúdo era WAV.
      // Medido em 2026-08-26: o arquivo da conversa 498 é `RIFF...WAVE`, PCM 16 bits 48 kHz, e
      // chamava-se `9e41aaf943c3.mp3`. O servidor identifica o tipo pelo CONTEÚDO (grava
      // `audio/x-wav`, correto) e fica com um nome que mente — o que confunde quem baixa, quem lê
      // o log e qualquer player que resolva confiar na extensão.
      //
      // Isto NÃO era a causa de o áudio não tocar no widget (a causa era o `Content-Disposition:
      // attachment`, consertado em `config/application.rb`), e o WAV continua sendo o formato certo
      // para os canais que não exigem MP3 — ele existe desde `c9cae01cb`, "fix: Support audio in
      // safari browser". Isto aqui é a coerência do nome, não o conserto do sintoma.
      const extensionByFormat = {
        [AUDIO_FORMATS.MP3]: 'mp3',
        [AUDIO_FORMATS.WAV]: 'wav',
      };
      const fileName = `${getUuid()}.${extensionByFormat[props.audioRecordFormat] || 'wav'}`;
      const file = new File([audioBlob], fileName, {
        type: props.audioRecordFormat,
      });
      // eslint-disable-next-line no-console
      console.log('[audio-debug] AudioRecorder:record-end:fileBuilt', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });
      wavesurfer.value.load(audioUrl);
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
      hasRecording.value = true;
      isRecording.value = false;
      // eslint-disable-next-line no-console
      console.log('[audio-debug] AudioRecorder:record-end:emitted', {
        hasRecording: hasRecording.value,
        isRecording: isRecording.value,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[audio-debug] AudioRecorder:record-end:error', {
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        original: err,
      });
      throw err;
    }
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  // eslint-disable-next-line no-console
  console.log('[audio-debug] AudioRecorder:stopRecording:called', {
    isRecording: isRecording.value,
    mediaRecorderState: record.value?.mediaRecorder?.state,
    mediaRecorderMimeType: record.value?.mediaRecorder?.mimeType,
  });
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  // eslint-disable-next-line no-console
  console.log('[audio-debug] AudioRecorder:startRecording:called', {
    audioRecordFormat: props.audioRecordFormat,
  });
  record.value.startRecording();
  isRecording.value = true;
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
